import { createFileRoute } from '@tanstack/react-router';
import { ArrowRight, MapPin, Stethoscope, Wrench } from 'lucide-react';
import { Button, DayCalendar } from '@bookaburra/ui';
import { usePageTitle } from '../components/usePageTitle.js';
import { MOCK_SCHEDULES, buildBookings } from '../lib/mock-schedules.js';

export const Route = createFileRoute('/demos')({
  component: DemosPage,
});

const APP_URL = 'https://app.bookaburra.com';

interface Demo {
  slug: string;
  name: string;
  blurb: string;
  details: string;
  Icon: typeof Stethoscope;
}

const demos: Demo[] = [
  {
    slug: 'sunshine-imaging',
    name: 'Sunshine Imaging',
    blurb: 'Radiology clinic — CT, MRI, ultrasound, X-ray.',
    details:
      'Ten staff with overlapping certifications across two ultrasound rooms, a CT-only room, a combined MRI/CT room, and an X-ray room. Bookaburra picks the right room and certified operator for each scan.',
    Icon: Stethoscope,
  },
  {
    slug: 'harbourside-medical',
    name: 'Harbourside Medical',
    blurb: 'GP + physio practice in St Kilda, Melbourne.',
    details:
      'GP standard and long consults, plus physio. Two consult rooms, one physio room. Same-day rebookings flow through the allocator so the desk never double-books.',
    Icon: MapPin,
  },
  {
    slug: 'two-mowers',
    name: 'Two Mowers Gardening',
    blurb: 'Mobile gardening team in Sydney.',
    details:
      'Lawn mowing, hedge trimming, garden tidy — on-site, travel-time aware. Suburbs and ride-on capability are factored into who can take which job.',
    Icon: Wrench,
  },
];

function DemosPage() {
  usePageTitle('Demos');

  return (
    <>
      {/* HERO */}
      <section className="bg-brand-cream">
        <div className="mx-auto max-w-6xl px-6 pb-12 pt-14 sm:px-12 sm:pt-20">
          <div className="inline-flex items-center gap-2 rounded-full bg-brand-cream-shadow px-3 py-1 text-xs font-semibold uppercase tracking-widest text-brand-sage ring-1 ring-brand-sage/15">
            Demos
          </div>
          <h1 className="mt-6 max-w-4xl text-4xl font-extrabold leading-[1.05] tracking-tightest text-brand-ink-soft sm:text-6xl lg:text-[4rem]">
            Try the booking flow on{' '}
            <span className="text-brand-sage">three made-up businesses.</span>
          </h1>
          <p className="mt-7 max-w-2xl text-lg text-brand-ink-soft/80 sm:text-xl">
            None of these are real customers — they&rsquo;re three example
            businesses we built so you can see how Bookaburra works. Pick a
            service, choose a slot, leave any name and contact. No signup
            needed.
          </p>
        </div>
      </section>

      {/* DEMO CARDS */}
      <section className="bg-brand-cream-shadow/30">
        <div className="mx-auto max-w-6xl px-6 py-16 sm:px-12 sm:py-20">
          <ul className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {demos.map((d) => (
              <li
                key={d.slug}
                className="flex flex-col rounded-2xl border border-brand-sage/15 bg-brand-cream p-7 shadow-[0_30px_80px_-30px_rgba(19,33,36,0.12)]"
              >
                <div className="mb-5 inline-flex h-10 w-10 items-center justify-center rounded-xl bg-brand-sage/10 text-brand-sage">
                  <d.Icon className="h-5 w-5" />
                </div>
                <h2 className="text-2xl font-bold tracking-tight text-brand-ink-soft">
                  {d.name}
                </h2>
                <p className="mt-2 text-sm font-medium text-brand-sage">
                  {d.blurb}
                </p>
                <p className="mt-4 flex-1 text-sm leading-relaxed text-brand-ink-soft/75">
                  {d.details}
                </p>
                <div className="mt-6 flex flex-wrap items-center gap-3">
                  <Button asChild>
                    <a href={`${APP_URL}/book/${d.slug}`}>
                      Open booking page
                      <ArrowRight className="h-3.5 w-3.5" aria-hidden />
                    </a>
                  </Button>
                  <Button asChild variant="outline">
                    <a href={`${APP_URL}/chat/${d.slug}`}>Booking assistant</a>
                  </Button>
                </div>
              </li>
            ))}
          </ul>

        </div>
      </section>

      {/* BEHIND THE SCENES — staff calendars */}
      <section className="bg-brand-cream">
        <div className="mx-auto max-w-6xl px-6 py-16 sm:px-12 sm:py-20">
          <div className="mb-10 max-w-2xl">
            <p className="text-xs font-semibold uppercase tracking-widest text-brand-sage">
              Behind the scenes
            </p>
            <h2 className="mt-3 text-3xl font-bold tracking-tight text-brand-ink-soft sm:text-4xl">
              What the day looks like on the inside
            </h2>
            <p className="mt-4 text-base text-brand-ink-soft/75">
              The same staff calendar your team sees — rendered live with
              illustrative data for each demo business, in their local time
              zone.
            </p>
          </div>

          <div className="grid gap-6 lg:grid-cols-3">
            {MOCK_SCHEDULES.map((s) => (
              <div key={s.slug} className="space-y-3">
                <div className="flex items-baseline justify-between gap-3">
                  <h3 className="text-sm font-semibold text-brand-ink-soft">
                    {s.name}
                  </h3>
                  <span className="text-[11px] uppercase tracking-widest text-brand-ink-soft/55">
                    {s.timezone.split('/')[1]?.replace('_', ' ')}
                  </span>
                </div>
                <DayCalendar
                  day={new Date()}
                  timezone={s.timezone}
                  panelLabel="Today"
                  cornerLabel="Demo"
                  bookings={buildBookings(s)}
                  staff={s.staff}
                />
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
